import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const PropertyPricingCard = ({
  customer,
  editing,
  editData,
  setEditData,
  liveCalc,
  formatCurrency,
  handleEditChange,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Property & Pricing</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>BHK Type</Label>
              {editing ? (
                <Select
                  value={editData.bhk_type || ""}
                  onValueChange={(value) => setEditData({ ...editData, bhk_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select BHK" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2BHK">2 BHK</SelectItem>
                    <SelectItem value="2.5BHK">2.5 BHK</SelectItem>
                    <SelectItem value="3BHK">3 BHK</SelectItem>
                    <SelectItem value="3.5BHK">3.5 BHK</SelectItem>
                    <SelectItem value="4BHK">4 BHK</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-slate-700 mt-1">{customer.bhk_type || "-"}</p>
              )}
            </div>
            <div>
              <Label>Floor</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editData.floor || ""}
                  onChange={(e) => handleEditChange('floor', parseInt(e.target.value) || 0)}
                  placeholder="Floor number"
                />
              ) : (
                <p className="text-slate-700 mt-1">{customer.floor || "0"}</p>
              )}
            </div>
            <div>
              <Label>Saleable Area (sq.ft)</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editData.saleable_area || ""}
                  onChange={(e) => handleEditChange('saleable_area', parseFloat(e.target.value) || 0)}
                />
              ) : (
                <p className="text-slate-700 mt-1">{customer.saleable_area || 0} sq.ft</p>
              )}
            </div>
            <div>
              <Label>Rate/Sq.ft (₹)</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editData.rate_per_sqft || ""}
                  onChange={(e) => handleEditChange('rate_per_sqft', parseFloat(e.target.value) || 0)}
                />
              ) : (
                <p className="text-slate-700 mt-1">₹{customer.rate_per_sqft?.toLocaleString() || 0}</p>
              )}
            </div>
            <div>
              <Label>Car Parking Charges</Label>
              <div className="mt-1 px-3 py-2 rounded-md border bg-slate-50 text-slate-700 font-medium" data-testid="car-parking-fixed-pricing">
                ₹2,00,000 (fixed)
              </div>
            </div>
            <div>
              <Label>Amenities</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editData.club_house_charges || 200000}
                  onChange={(e) => setEditData({ ...editData, club_house_charges: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="edit-amenities"
                />
              ) : (
                <p className="text-slate-700 mt-1">{formatCurrency(customer.club_house_charges)}</p>
              )}
            </div>
            <div>
              <Label>Additional Charges</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editData.additional_charges || 0}
                  onChange={(e) => setEditData({ ...editData, additional_charges: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  placeholder="Enter additional charges"
                />
              ) : (
                <p className="text-slate-700 mt-1">{formatCurrency(customer.additional_charges || 0)}</p>
              )}
            </div>
            <div>
              <Label>Labour Cess (0.70%)</Label>
              <p className="text-slate-700 mt-1">
                {editing && liveCalc ? formatCurrency(liveCalc.labourCess) : formatCurrency(customer.labour_cess)}
              </p>
            </div>
            <div>
              <Label>GST (5%)</Label>
              <p className="text-slate-700 mt-1">
                {editing && liveCalc ? formatCurrency(liveCalc.gst) : formatCurrency(customer.gst_amount)}
              </p>
            </div>
            <div>
              <Label>Interest Amount</Label>
              {editing ? (
                <>
                  <Input
                    type="number"
                    value={editData.interest_amount ?? ""}
                    onChange={(e) => setEditData({ ...editData, interest_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className="mt-1"
                    data-testid="interest-amount-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Manual entry · added after GST (non GST-taxable)</p>
                </>
              ) : (
                <p className="text-slate-700 mt-1" data-testid="interest-amount-value">
                  {formatCurrency(customer.interest_amount || 0)}
                </p>
              )}
            </div>
            <div>
              <Label>BESCOM Charges (₹/sq.ft)</Label>
              {editing ? (
                <>
                  <Input
                    type="number"
                    value={editData.bescom_charges_per_sqft ?? 100}
                    onChange={(e) => setEditData({ ...editData, bescom_charges_per_sqft: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                    placeholder="100"
                    className="mt-1"
                    data-testid="bescom-rate-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Manual entry · default ₹100/sq.ft · Subtotal: {formatCurrency(
                      ((editData.bescom_charges_per_sqft === null || editData.bescom_charges_per_sqft === undefined || editData.bescom_charges_per_sqft === "")
                        ? 100
                        : (parseFloat(editData.bescom_charges_per_sqft) || 0)
                      ) * (parseFloat(editData.saleable_area) || 0)
                    )} (GST applicable)
                  </p>
                </>
              ) : (
                <p className="text-slate-700 mt-1" data-testid="bescom-total-value">
                  {(() => {
                    const r = customer.bescom_charges_per_sqft;
                    const rate = (r === null || r === undefined) ? 100 : r;
                    const area = customer.saleable_area || 0;
                    return formatCurrency(rate * area);
                  })()}
                  <span className="text-xs text-slate-500 ml-2">
                    (₹{customer.bescom_charges_per_sqft != null ? customer.bescom_charges_per_sqft : 100}/sq.ft × {customer.saleable_area || 0} sq.ft)
                  </span>
                </p>
              )}
            </div>
            <div>
              <Label>Total Price</Label>
              <p className={`font-bold mt-1 ${editing && liveCalc ? 'text-green-600' : 'text-primary'}`}>
                {editing && liveCalc ? formatCurrency(liveCalc.total) : formatCurrency(customer.total_price)}
                {editing && liveCalc && liveCalc.total !== customer.total_price && (
                  <span className="text-xs font-normal text-slate-500 ml-2">(live preview)</span>
                )}
              </p>
            </div>
            <div>
              <Label>UDS</Label>
              <p className="text-slate-700 mt-1">
                {editing && liveCalc ? liveCalc.uds : (customer.uds || "-")}
              </p>
            </div>
          </div>
          {editing && liveCalc && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700 font-medium mb-2">Live Price Preview</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span>Base Price ({editData.saleable_area || 0} × ₹{editData.rate_per_sqft || 0}):</span>
                <span className="font-medium text-right">{formatCurrency(liveCalc.basePrice)}</span>
                <span>Car Parking (fixed):</span>
                <span className="font-medium text-right">{formatCurrency(liveCalc.parkingCharges)}</span>
                <span>Amenities:</span>
                <span className="font-medium text-right">{formatCurrency(liveCalc.clubHouse)}</span>
                {liveCalc.additionalCharges > 0 && (
                  <>
                    <span>Additional Charges:</span>
                    <span className="font-medium text-right">{formatCurrency(liveCalc.additionalCharges)}</span>
                  </>
                )}
                {liveCalc.bescomTotal > 0 && (
                  <>
                    <span>BESCOM ({editData.saleable_area || 0} sq.ft × ₹{editData.bescom_charges_per_sqft || 0}/sq.ft):</span>
                    <span className="font-medium text-right">{formatCurrency(liveCalc.bescomTotal)}</span>
                  </>
                )}
                <span className="text-slate-500 text-xs pt-1">Labour Cess (0.70%) + GST (5%) on subtotal above</span>
                <span></span>
                {liveCalc.interestAmount > 0 && (
                  <>
                    <span>Interest Amount (post-GST):</span>
                    <span className="font-medium text-right">{formatCurrency(liveCalc.interestAmount)}</span>
                  </>
                )}
                <span className="font-semibold pt-2 border-t">New Total:</span>
                <span className="font-bold text-right text-green-700 pt-2 border-t">{formatCurrency(liveCalc.total)}</span>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Price updates automatically as you edit. Click "Save Changes" to persist.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyPricingCard;
